import { FilePurpose, LanguagesEnum } from '@/api/enums';
import { phishingLanguagesDropdown } from '@/api/enums/phishing-languages.enum';
import { Campaign, CampaignFormValues } from '@/client/campaigns';
import { File } from '@/client/files';
import { DialogContext } from '@/common/context';
import {
  FormikDropdown,
  FormikInput,
  FormikRichTextArea,
  FormikWrappedSwitch,
} from '@/components/form';
import { BenchmarksSelectInput } from '@/components/form/selectors/BenchmarksSelectInput';
import { useCampaignMasterTemplates } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { CheckboxesContainer } from '@/ui/checkboxes-container';
import { EditableDropdownItem } from '@/ui/editable-dropdown/EditableDropdown';
import { MediaUpload } from '@/ui/media-upload';
import { FlexContainer } from '@/ui/styled-ui';
import { getPhishingSignOptions } from '@/utils/helpers';
import {
  difficultyOptions,
  preventDropdownBrowserTranslate,
  senderOptions,
} from '@/utils/helpers/ui.helper';
import { Field, FormikErrors } from 'formik';
import { Tooltip } from 'primereact/tooltip';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UsersScore } from '../UsersScore';

const InheritanceBanner = styled.div`
  margin-top: var(--xsmall-padding);
  padding: 10px 14px;
  background-color: var(--beige-main);
  border-radius: var(--small-border-radius);
  font-size: var(--xsmall-font-size);
  color: var(--black-main);
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
`;

type InfoStepProps = {
  values: CampaignFormValues;
  errors: FormikErrors<CampaignFormValues>;
  valuesSetter: any;
  preselectedThumbnail?: File | null;
  campaign?: Campaign;
};

export const InfoStep: React.FC<InfoStepProps> = ({
  values,
  errors,
  valuesSetter,
  preselectedThumbnail,
  campaign,
}) => {
  const { t } = useTranslation();
  const [canEditName, setCanEditName] = useState(false);
  const account = useAppSelector(selectCurrentAccount);
  const { masterTemplates } = useCampaignMasterTemplates({ take: 1000 }, {
    enabled: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  } as any);
  const masterTemplateOptions = useMemo(
    () =>
      (masterTemplates?.result ?? [])
        .filter((mt) => mt.active)
        .map((mt) => ({
          label:
            mt.translations?.find(
              (tr) => tr.language === LanguagesEnum.EN && tr.name,
            )?.name ??
            mt.translations?.find((tr) => tr.name)?.name ??
            mt.id,
          value: mt.id,
        })),
    [masterTemplates?.result],
  );
  const selectedMasterTemplate = values.masterTemplate
    ? (masterTemplates?.result ?? []).find(
        (mt) => mt.id === values.masterTemplate,
      )
    : null;

  const masterOverrides = selectedMasterTemplate
    ? {
        name:
          selectedMasterTemplate.translations?.find(
            (tr) => tr.language === values.language && tr.name,
          )?.name ??
          selectedMasterTemplate.translations?.find(
            (tr) => tr.language === LanguagesEnum.EN && tr.name,
          )?.name ??
          '',
        description:
          selectedMasterTemplate.translations?.find(
            (tr) => tr.language === values.language && tr.description,
          )?.description ??
          selectedMasterTemplate.translations?.find(
            (tr) => tr.language === LanguagesEnum.EN && tr.description,
          )?.description ??
          selectedMasterTemplate.translations?.find((tr) => tr.description)
            ?.description ??
          '',
        difficulty: selectedMasterTemplate.difficulty ?? undefined,
        phishingSign: selectedMasterTemplate.phishingSign?.key ?? undefined,
        benchmarkId: selectedMasterTemplate.benchmark?.id ?? undefined,
      }
    : null;

  const { setDialogData } = useContext(DialogContext);

  const handleWhitelistingTestToggle = (checked: boolean) => {
    if (!checked) {
      setDialogData({
        show: true,
        type: DialogType.WARNING,
        header: t('campaign.whitelisting.test.unmark.header'),
        message: t('campaign.whitelisting.test.unmark.message'),
        onAccept: () => {
          valuesSetter('isWhitelistingTest', false);
        },
      });
      return;
    }

    valuesSetter('isWhitelistingTest', true);
  };

  const isPublishDisabled =
    !values.isPublished && Object.keys(errors).length > 0;

  return (
    <>
      <h2>{t('campaign.template.info')}</h2>
      <FlexContainer gap={24} className="mb-5 pb-5 h-full" align="stretch">
        <div className="flex-1 pb-5 mb-5">
          <div className="field w-full mb-4">
            <Field
              id="masterTemplate"
              label={t('simulationTemplates.masterTemplate')}
              name="masterTemplate"
              className="w-full"
              component={FormikDropdown}
              placeholder={t('simulationTemplates.selectMasterTemplate')}
              options={masterTemplateOptions}
              filter
              showClear
            />
            {selectedMasterTemplate && (
              <InheritanceBanner>
                <i className="pi pi-lock" style={{ opacity: 0.5 }} />
                {t('simulationTemplates.inheritedFromMaster')}
              </InheritanceBanner>
            )}
          </div>
          <FlexContainer
            justify="flex-start"
            align="flex-end"
            className="mb-4"
            style={
              selectedMasterTemplate
                ? { opacity: 0.6, pointerEvents: 'none' }
                : undefined
            }
          >
            <div className="field w-full m-0 relative">
              <Field
                id="name"
                name="name"
                label={t('generic.name')}
                value={masterOverrides?.name ?? values.name}
                className="w-full"
                component={FormikInput}
                disabled={!canEditName || !!selectedMasterTemplate}
                data-testid="campaign-form-info-step-name"
              />
            </div>
            {!selectedMasterTemplate && (
              <AppButton
                icon="pi pi-pencil"
                type="text"
                onClick={() => setCanEditName(true)}
                isDisabled={canEditName}
                data-testid="campaign-form-info-step-enable-edit-name"
              />
            )}
          </FlexContainer>
          <FlexContainer gap={24} align="flex-start">
            <div className="field w-full mb-4 flex-1">
              <Field
                id="language"
                label={t('generic.lang')}
                name="language"
                required
                filter
                className="w-full"
                component={FormikDropdown}
                placeholder={t('generic.select')}
                options={phishingLanguagesDropdown}
                {...preventDropdownBrowserTranslate}
              />
            </div>
            <div
              className="field w-full mb-4 flex-1"
              style={
                selectedMasterTemplate
                  ? { opacity: 0.6, pointerEvents: 'none' }
                  : undefined
              }
            >
              <Field
                id="difficulty"
                label={t('generic.difficulty')}
                name="difficulty"
                required
                className="w-full"
                component={FormikDropdown}
                disabled={
                  !!selectedMasterTemplate ||
                  (!!values?.benchmarkSummary &&
                    values.benchmarkSummary.clickedLinksPercentage != null &&
                    values.benchmarkSummary.submittedDataPercentage != null)
                }
                placeholder={t('generic.select')}
                options={difficultyOptions}
                value={masterOverrides?.difficulty ?? values.difficulty}
              />
            </div>
          </FlexContainer>
          <div className="field w-full mb-4">
            <Field
              id="senderProfileType"
              label={t('generic.sender')}
              name="senderProfileType"
              required
              className="w-full"
              component={FormikDropdown}
              placeholder={t('generic.select')}
              options={senderOptions(t)}
            />
          </div>
          <div
            className="field w-full mb-4"
            style={
              selectedMasterTemplate
                ? { opacity: 0.6, pointerEvents: 'none' }
                : undefined
            }
          >
            <Field
              id="phishingSign"
              label={t('campaign.template.phishingSign')}
              name="phishingSign"
              className="w-full"
              component={FormikDropdown}
              placeholder={t('generic.select')}
              options={getPhishingSignOptions(t)}
              disabled={!!selectedMasterTemplate}
              value={masterOverrides?.phishingSign ?? values.phishingSign}
            />
          </div>

          <div
            className="field w-full mb-4"
            style={
              selectedMasterTemplate
                ? { pointerEvents: 'none', opacity: 0.6 }
                : undefined
            }
          >
            <label htmlFor="benchmark">
              {t('campaign.template.phishingType')}
            </label>
            <BenchmarksSelectInput
              value={masterOverrides?.benchmarkId ?? values.benchmark?.id}
              placeholder={t('generic.select')}
              onChange={(item: EditableDropdownItem | null) =>
                valuesSetter('benchmark', item)
              }
            />
          </div>

          {!!values?.benchmarkSummary && (
            <>
              <label htmlFor="usersScore">{t('campaigns.userScore')} </label>
              <FlexContainer gap={24} className="mb-4 mt-2" align="stretch">
                <UsersScore benchmark={values.benchmarkSummary} />
              </FlexContainer>
            </>
          )}

          <div
            style={
              selectedMasterTemplate && !selectedMasterTemplate.active
                ? { opacity: 0.6, pointerEvents: 'none' as const }
                : undefined
            }
          >
            <CheckboxesContainer
              className="toggle-container mb-4 mt-2"
              id="isTemplate-active"
            >
              <Field
                disabled={
                  isPublishDisabled ||
                  (!!selectedMasterTemplate && !selectedMasterTemplate.active)
                }
                inputId="isPublished"
                name="isPublished"
                label={t('campaign.template.isTemplate')}
                component={FormikWrappedSwitch}
              />
            </CheckboxesContainer>
            {isPublishDisabled && (
              <Tooltip
                position="mouse"
                target="#isTemplate-active"
                content={`${t('campaign.template.selectAll')}\n${Object.values(
                  errors,
                ).join(', \n')}`}
              />
            )}
          </div>
          {account?.isSystem && campaign?.id && (
            <CheckboxesContainer
              className="toggle-container mb-4 mt-2"
              id="isWhitelistingTest-active"
            >
              <Field
                inputId="isWhitelistingTest"
                name="isWhitelistingTest"
                label={t('campaign.whitelisting.test.toggle')}
                component={FormikWrappedSwitch}
                onChange={(e: { value: boolean }) =>
                  handleWhitelistingTestToggle(!!e.value)
                }
              />
            </CheckboxesContainer>
          )}
          <div className="field w-full mb-4">
            <MediaUpload
              label={t('campaign.template.thumbnail')}
              description={t('campaign.template.thumbnail.description')}
              purpose={FilePurpose.THUMBNAIL}
              accept=".png,.jpg,.jpeg"
              maxFileSize={2_000_000}
              displayMode="image"
              onFileUpload={(file: File | null) =>
                valuesSetter('thumbnail', file?.id)
              }
              preselectedFile={preselectedThumbnail}
              noFileText={t('campaign.template.no.thumbnail')}
              uploadButtonText={t('campaign.template.upload.thumbnail')}
              isDisabled={!!selectedMasterTemplate}
            />
          </div>
        </div>
        <div className="flex-1 h-auto field mb-0">
          {masterOverrides ? (
            <>
              <label>{t('generic.description')}</label>
              <div
                style={{
                  padding: 'var(--medium-padding)',
                  backgroundColor: 'var(--beige-main)',
                  borderRadius: 'var(--small-border-radius)',
                  minHeight: '200px',
                  opacity: 0.6,
                }}
                dangerouslySetInnerHTML={{
                  __html: masterOverrides.description || '',
                }}
              />
            </>
          ) : (
            <Field
              id="rich-text-editor"
              label={t('generic.description')}
              name="description"
              className="w-full"
              placeholder={t('generic.description')}
              component={FormikRichTextArea}
            />
          )}
        </div>
      </FlexContainer>
    </>
  );
};
